import { DateTime } from 'luxon'
import { daysToMilliseconds, type AppAlertState } from '@labrav/common'
import {
  OfferStatus,
  type PrivateOffer,
} from '../../../modules/privateOffer/types'
import { CloudMarketplace } from '../../../modules/productsListing/types'
import { isOfferExpired } from '../../../common/utils/isOfferExpired'
import {
  offerCancellationInProgressMessage,
  offerCancelledMessage,
  offerExpiredMessage,
  offerExpiringSoonMessage,
  offerFailedMessage,
} from '../../../common/utils/messagesContants'

export const offerExpireInDaysCount = 5

export const getPayloadForCancelPrivateOffer = (
  cloudType: CloudMarketplace,
  privateOffer: PrivateOffer
) => {
  switch (cloudType) {
    case 'AWS': {
      return {
        offerStatus: OfferStatus.PRIVATE_OFFER_RESTRICTION_IN_PROGRESS,
      }
    }
    case 'AZURE': {
      return {
        state: 'draft',
        operation: 'UPDATE',
      }
    }
    case 'GCP': {
      return {
        cancelPrivateOffer: true,
        offerStatus: privateOffer?.offerStatus,
        cloudOfferId: privateOffer?.cloudOfferId,
      }
    }
    case 'REDHAT': {
      return {}
    }
  }
}

export interface IsExpiringInNDaysProps {
  expirationDate: Date
  status?: OfferStatus
  now?: () => DateTime
  expiringDays?: number
}

export const isExpiringInNDays = ({
  expirationDate,
  status,
  now = DateTime.now,
  expiringDays = offerExpireInDaysCount,
}: IsExpiringInNDaysProps) => {
  const milliseconds = daysToMilliseconds(expiringDays)

  const expiryDateAndTime = DateTime.fromISO(
    expirationDate.toISOString()
  ).endOf('day')
  const currentDateTime = now().endOf('day')

  const timeDifference = expiryDateAndTime
    .diff(currentDateTime)
    .as('milliseconds')

  if (
    timeDifference < milliseconds &&
    timeDifference >= 0 &&
    status !== OfferStatus.PRIVATE_OFFER_ACCEPTED
  ) {
    return true
  }
  return false
}

export const getPrivateOfferStatusMessage = ({
  status,
  expirationDate,
}: {
  status: OfferStatus
  expirationDate: Date
}) => {
  switch (status) {
    case OfferStatus.OFFER_CREATION_FAILED:
      return 'FAILED'
    case OfferStatus.PRIVATE_OFFER_RESTRICTION_IN_PROGRESS:
      return 'CANCELLATION IN PROGRESS'
    case OfferStatus.PRIVATE_OFFER_RESTRCITED:
      return 'CANCELLED'
    default:
      if (isExpiringInNDays({ expirationDate, status })) {
        return 'EXPIRING SOON'
      }
      if (isOfferExpired(expirationDate, status)) {
        return 'EXPIRED'
      }
      return ''
  }
}

export const getAlertMessage = (
  status: OfferStatus,
  expirationDate: Date
): AppAlertState => {
  switch (status) {
    case OfferStatus.PRIVATE_OFFER_FAILED:
      return { message: offerFailedMessage, messageType: 'ERROR' }
    case OfferStatus.PRIVATE_OFFER_RESTRCITED:
      return { message: offerCancelledMessage, messageType: 'ERROR' }
    case OfferStatus.PRIVATE_OFFER_RESTRICTION_IN_PROGRESS:
      return {
        message: offerCancellationInProgressMessage,
        messageType: 'WARNING',
      }
    default:
      if (isExpiringInNDays({ expirationDate, status })) {
        return { message: offerExpiringSoonMessage, messageType: 'WARNING' }
      } else if (isOfferExpired(expirationDate, status)) {
        return { message: offerExpiredMessage, messageType: 'ERROR' }
      } else {
        return { message: '', messageType: 'NONE' }
      }
  }
}
